import { motion } from "framer-motion";
import { Landmark, Wallet, CreditCard, ArrowLeftRight, Braces } from "lucide-react";

/*
  Abstract technical banking visualization.
  A central CashFUX core node connects to Accounts, Wallets, Cards, Payments and API nodes.
  Glowing dots travel along the connecting paths to represent payment flows.
*/

const NODES = [
  { id: "accounts", label: "Konten", Icon: Landmark, x: 90, y: 70 },
  { id: "wallets", label: "Wallets", Icon: Wallet, x: 90, y: 300 },
  { id: "cards", label: "Karten", Icon: CreditCard, x: 430, y: 60 },
  { id: "payments", label: "Payments", Icon: ArrowLeftRight, x: 440, y: 320 },
  { id: "api", label: "API", Icon: Braces, x: 470, y: 190 },
];

const CORE = { x: 265, y: 195 };

// Bezier-ish paths from core to each node
const PATHS = [
  `M${CORE.x},${CORE.y} C 200,120 160,90 ${90 + 26},${70 + 20}`,
  `M${CORE.x},${CORE.y} C 200,270 160,300 ${90 + 26},${300 + 20}`,
  `M${CORE.x},${CORE.y} C 340,140 390,90 ${430 + 26},${60 + 20}`,
  `M${CORE.x},${CORE.y} C 350,260 400,320 ${440 + 26},${320 + 20}`,
  `M${CORE.x},${CORE.y} C 370,195 420,190 ${470 + 26},${190 + 20}`,
];

export default function BankingVisual() {
  return (
    <div className="relative w-full">
      {/* faint bounding frame corners */}
      <div className="pointer-events-none absolute inset-0">
        <span className="absolute left-0 top-0 h-4 w-4 border-l border-t border-white/20" />
        <span className="absolute right-0 top-0 h-4 w-4 border-r border-t border-white/20" />
        <span className="absolute left-0 bottom-0 h-4 w-4 border-l border-b border-white/20" />
        <span className="absolute right-0 bottom-0 h-4 w-4 border-r border-b border-white/20" />
      </div>

      <svg
        viewBox="0 0 560 400"
        className="w-full h-auto"
        fill="none"
        aria-hidden="true"
        data-testid="banking-visual"
      >
        <defs>
          <radialGradient id="coreGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#e01e37" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#e01e37" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#e01e37" stopOpacity="0.05" />
            <stop offset="100%" stopColor="#e01e37" stopOpacity="0.5" />
          </linearGradient>
          <filter id="soft" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" />
          </filter>
        </defs>

        {/* core glow */}
        <circle cx={CORE.x} cy={CORE.y} r="120" fill="url(#coreGlow)" />

        {/* connecting paths */}
        {PATHS.map((d, i) => (
          <g key={i}>
            <motion.path
              d={d}
              stroke="url(#lineGrad)"
              strokeWidth="1.25"
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: 0.3 + i * 0.15, ease: "easeInOut" }}
            />
          </g>
        ))}

        {/* flowing dots along paths */}
        {PATHS.map((d, i) => (
          <g key={`dot-${i}`}>
            <circle r="3.2" fill="#ff3b52" filter="url(#soft)">
              <animateMotion dur={`${3.2 + i * 0.6}s`} repeatCount="indefinite" path={d} />
              <animate attributeName="opacity" values="0;1;1;0" dur={`${3.2 + i * 0.6}s`} repeatCount="indefinite" />
            </circle>
            <circle r="2" fill="#ffffff">
              <animateMotion dur={`${3.2 + i * 0.6}s`} repeatCount="indefinite" path={d} />
              <animate attributeName="opacity" values="0;1;1;0" dur={`${3.2 + i * 0.6}s`} repeatCount="indefinite" />
            </circle>
          </g>
        ))}

        {/* outer nodes */}
        {NODES.map((n, i) => (
          <g key={n.id}>
            <motion.g
              initial={{ opacity: 0, scale: 0.6 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.6 + i * 0.12 }}
            >
              <rect x={n.x} y={n.y} width="52" height="40" fill="#0a0c12" stroke="rgba(255,255,255,0.12)" />
              <rect x={n.x} y={n.y} width="52" height="2" fill="#e01e37" opacity="0.6" />
            </motion.g>
          </g>
        ))}

        {/* core node */}
        <motion.g
          initial={{ opacity: 0, scale: 0.4 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "backOut" }}
        >
          <rect x={CORE.x - 42} y={CORE.y - 26} width="84" height="52" fill="#e01e37" />
          <rect x={CORE.x - 42} y={CORE.y - 26} width="84" height="52" fill="none" stroke="#ff5a6e" strokeWidth="1" />
        </motion.g>
      </svg>

      {/* HTML overlays for crisp icons + labels (positioned over svg via percentage) */}
      <div className="pointer-events-none absolute inset-0">
        {NODES.map((n) => (
          <div
            key={n.id}
            className="absolute flex flex-col items-center"
            style={{ left: `${((n.x + 26) / 560) * 100}%`, top: `${((n.y + 20) / 400) * 100}%`, transform: "translate(-50%,-50%)" }}
          >
            <n.Icon className="h-4 w-4 text-white/80" strokeWidth={1.5} />
            <span className="mt-6 font-mono text-[9px] uppercase tracking-[0.15em] text-white/50 whitespace-nowrap">
              {n.label}
            </span>
          </div>
        ))}
        <div
          className="absolute flex flex-col items-center"
          style={{ left: `${(CORE.x / 560) * 100}%`, top: `${(CORE.y / 400) * 100}%`, transform: "translate(-50%,-50%)" }}
        >
          <span className="font-display text-sm font-extrabold tracking-tight text-white">CashFUX</span>
          <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/70">Ledger Core</span>
        </div>
      </div>
    </div>
  );
}
